import { useState, useCallback } from 'react';
import { message } from 'antd';
import { c505Api, PendingItem } from '../../../services/c505Api';

export interface ItemState {
  item: PendingItem;
  selected: boolean;
}

export function useC505Query() {
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);

  /** Query pending sales return orders by range */
  const handleQuery = useCallback(async (startNo: string, endNo: string) => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await c505Api.query(startNo.trim(), endNo.trim());
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的待核准銷貨退回單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Toggle checkbox for a single item */
  const handleToggleItem = useCallback((returnNo: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fc130_01 === returnNo ? { ...s, selected: checked } : s
      )
    );
  }, []);

  /** Select all / deselect all (only items that can_approve) */
  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) =>
      prev.map((s) => ({
        ...s,
        selected: s.item.can_approve ? checked : s.selected,
      }))
    );
  }, []);

  /** Reset items and queried state after approval */
  const resetItems = useCallback(() => {
    setItems([]);
    setQueried(false);
  }, []);

  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected =
    items.length > 0 && items.every((s) => s.selected || !s.item.can_approve);

  return {
    items,
    loading,
    queried,
    selectedCount,
    allSelected,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    resetItems,
  };
}
